import React, { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { canvasService, progressService } from '../api/services';
import useApi from '../hooks/useApi';
import DashboardLayout from '../components/layout/DashboardLayout';
import { PenLine, Upload, FileText, X, AlertCircle, CheckCircle2, Lock, Loader2, Star, Film, Music, Image, ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';

// ── File type helpers ────────────────────────────────────────
const getFileCategory = (file) => {
  const type = file.type || '';
  if (type.startsWith('video/')) return 'video';
  if (type.startsWith('audio/')) return 'audio';
  if (type.startsWith('image/')) return 'image';
  return 'document';
};

const getFileIcon = (file) => {
  const cat = getFileCategory(file);
  if (cat === 'video') return Film;
  if (cat === 'audio') return Music;
  if (cat === 'image') return Image;
  return FileText;
};

const formatFileSize = (bytes) => {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const FILE_CATEGORY_COLOR = {
  video: 'text-purple-600 bg-purple-50',
  audio: 'text-blue-600 bg-blue-50',
  image: 'text-green-600 bg-green-50',
  document: 'text-forest-600 bg-forest-50',
};

const StatusBadge = ({ status }) => {
  const styles = {
    PENDING: 'bg-cream-100 text-forest-400 border-cream-200',
    SUBMITTED: 'bg-amber-50 text-amber-700 border-amber-200',
    GRADED: 'bg-forest-50 text-forest-700 border-forest-200',
    REVISION_REQUESTED: 'bg-orange-50 text-orange-700 border-orange-200',
  };
  return (
    <span className={`text-[10px] font-semibold border px-3 py-1 rounded-full ${styles[status] ?? ''}`}>{status}</span>
  );
};

const CanvasPage = () => {
  // Get levelId from URL params or progress
  const { levelId } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [text, setText] = useState('');
  const [answers, setAnswers] = useState([]);  // per-question answers array
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const { data: canvasData, loading, error, refetch } = useApi(
    () => canvasService.getForLevel(levelId),
    [levelId],
  );

  const activity = canvasData?.activity ?? null;
  const submission = canvasData?.submission ?? null;
  const isUnlocked = canvasData?.isUnlocked ?? false;

  // Parse instructions into answerable questions only.
  // A line is treated as a question if it:
  //   - ends with "?" OR
  //   - starts with an action verb (Describe, List, Explain, What, How, Why, etc.) OR
  //   - is explicitly numbered AND starts with an action verb or ends with "?"
  // Pure descriptive/context lines (headings, explanations) are NOT treated as questions.
  const parseQuestions = (instructions) => {
    if (!instructions?.trim()) return [];

    const ACTION_VERBS = [
      'describe', 'list', 'explain', 'what', 'how', 'why', 'who', 'when', 'where',
      'identify', 'outline', 'summarise', 'summarize', 'provide', 'discuss',
      'analyse', 'analyze', 'evaluate', 'compare', 'define', 'state', 'write',
      'upload', 'attach', 'submit', 'demonstrate', 'show', 'tell', 'share',
      'create', 'develop', 'design', 'build', 'draft', 'prepare', 'present',
    ];

    const lines = instructions.split('\n').map(l => l.trim()).filter(Boolean);
    const questions = [];

    for (const line of lines) {
      // Strip leading number/bullet: "1." "1)" "-" "*" "•"
      const cleaned = line.replace(/^(\d+[\.\)\s]+|[-*•]\s+)/, '').trim();
      if (cleaned.length < 8) continue;

      const lower = cleaned.toLowerCase();
      const endsWithQuestion = cleaned.endsWith('?');
      const startsWithAction = ACTION_VERBS.some(v => lower.startsWith(v));

      if (endsWithQuestion || startsWithAction) {
        questions.push(cleaned);
      }
    }

    return questions;
  };

  const questions = parseQuestions(activity?.instructions);

  const handleFileAdd = (e) => {
    const newFiles = Array.from(e.target.files ?? []);
    const combined = [...files, ...newFiles].slice(0, 5);
    setFiles(combined);
    e.target.value = '';
  };

  const removeFile = (idx) => setFiles((f) => f.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate — if structured questions, at least one must be answered
    if (questions.length > 0) {
      const hasAnswer = answers.some(a => a?.trim());
      if (!hasAnswer && files.length === 0) {
        toast.error('Please answer at least one question or upload a file');
        return;
      }
    } else {
      if (!text.trim() && files.length === 0) {
        toast.error('Please add your submission text or at least one file');
        return;
      }
    }

    setSubmitting(true);
    try {
      // Build submissionText — structured JSON if questions, plain text otherwise
      let submissionText;
      if (questions.length > 0) {
        const structured = questions.map((q, i) => ({
          question: q,
          answer: answers[i]?.trim() ?? '',
        }));
        submissionText = JSON.stringify(structured);
      } else {
        submissionText = text;
      }

      const formData = new FormData();
      formData.append('canvasActivityId', activity.id);
      formData.append('submissionText', submissionText);
      files.forEach((f) => formData.append('files', f));
      await canvasService.submit(formData);
      toast.success('Submission sent! Your expert will review and grade it.');
      refetch();
    } catch (err) {
      const specific = err.response?.data?.errors?.[0]?.message;
      toast.error(specific || err.response?.data?.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <DashboardLayout title=" Tourent Cycle">
      <div className="max-w-2xl space-y-4 animate-pulse">
        <div className="h-40 bg-white border border-cream-200 rounded-2xl" />
        <div className="h-60 bg-white border border-cream-200 rounded-2xl" />
      </div>
    </DashboardLayout>
  );

  if (error || !activity) return (
    <DashboardLayout title="Tourent Cycle">
      <div className="max-w-2xl">
        <div className="bg-white border border-cream-200 rounded-2xl p-10 text-center space-y-3">
          <AlertCircle size={36} className="mx-auto text-rust-400" />
          <h3 className="font-serif text-xl text-forest-900">No canvas activity found</h3>
          <p className="text-sm text-forest-500">
            {error ? 'Could not load canvas — make sure your API server is running.' : 'This level has no canvas activity yet.'}
          </p>
        </div>
      </div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout title="Tourent Cycle Submission">
      <div className="max-w-2xl space-y-6">

        {/* Back to My Progress */}
        <button
          onClick={() => navigate('/progress')}
          className="flex items-center gap-2 text-sm text-forest-500 hover:text-forest-900 transition-colors group">
          <ArrowLeft size={15} className="group-hover:-translate-x-1 transition-transform" />
          Back to My Progress
        </button>

        {/* Activity info */}
        <div className="bg-white border border-cream-200 rounded-2xl p-6 space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="font-serif text-xl font-semibold text-forest-900">{activity.title}</h2>
              <p className="text-xs text-forest-500 mt-1 capitalize">{activity.activityType?.toLowerCase()}</p>
            </div>
            {submission && <StatusBadge status={submission.status} />}
          </div>

          {activity.description && (
            <p className="text-sm text-forest-600 leading-relaxed">{activity.description}</p>
          )}

          {activity.instructions && (
            <div className="bg-cream-50 border border-cream-100 rounded-xl p-4">
              <p className="text-xs font-semibold text-forest-700 mb-2 uppercase tracking-widest">Instructions</p>
              <p className="text-sm text-forest-600 whitespace-pre-line leading-relaxed">{activity.instructions}</p>
            </div>
          )}

          <div className="flex flex-wrap gap-4 text-xs text-forest-500">
            <span>Max score: <strong className="text-forest-800">{activity.maxScore}</strong></span>
            <span>Passing score: <strong className="text-forest-800">{activity.passingScore}</strong></span>
            {activity.dueInDays && <span>Due in: <strong className="text-forest-800">{activity.dueInDays} days</strong></span>}
            {activity.allowedFileTypes && <span>Accepted: <strong className="text-forest-800">{activity.allowedFileTypes.join(', ')}</strong></span>}
          </div>
        </div>

        {/* Locked state */}
        {!isUnlocked && !submission && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 flex items-start gap-4">
            <Lock size={20} className="text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-sans font-semibold text-amber-800 text-sm">Canvas is locked</h4>
              <p className="text-xs text-amber-700 mt-1 leading-relaxed">
                You need to complete all content <strong>and</strong> attend a live session before you can submit your canvas assignment.
              </p>
            </div>
          </div>
        )}

        {/* Existing submission result */}
        {submission && (
          <div className={`border rounded-2xl p-6 space-y-4 ${submission.status === 'GRADED' ? 'bg-forest-50 border-forest-200' : 'bg-white border-cream-200'}`}>
            <div className="flex items-center justify-between">
              <h3 className="font-serif font-semibold text-forest-900">Your Submission</h3>
              <StatusBadge status={submission.status} />
            </div>

            {/* View-only banner when pending review */}
            {submission.status === 'SUBMITTED' && (
              <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                <span className="text-amber-500 text-base flex-shrink-0">👁</span>
                <div>
                  <p className="text-xs font-semibold text-amber-800">Currently under review</p>
                  <p className="text-xs text-amber-700 mt-0.5 leading-relaxed">
                    Your submission has been received and is awaiting expert review. You can view what you submitted below.
                  </p>
                </div>
              </div>
            )}

            {submission.submissionText && (() => {
              // Try to parse as structured Q&A JSON
              let parsed = null;
              try { parsed = JSON.parse(submission.submissionText); } catch { parsed = null; }
              if (Array.isArray(parsed) && parsed[0]?.question) {
                return (
                  <div className="space-y-4">
                    {parsed.map((qa, i) => (
                      <div key={i} className="space-y-1.5">
                        <div className="flex items-start gap-2">
                          <span className="w-5 h-5 bg-forest-800 text-cream-50 rounded-full text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                          <p className="text-xs font-semibold text-forest-700">{qa.question}</p>
                        </div>
                        <div className="ml-7 bg-cream-50 border border-cream-200 rounded-xl px-4 py-3">
                          <p className="text-sm text-forest-700 leading-relaxed whitespace-pre-line">
                            {qa.answer || <span className="text-forest-300 italic">No answer provided</span>}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              }
              return <p className="text-sm text-forest-600 leading-relaxed">{submission.submissionText}</p>;
            })()}

            {(submission.fileUrls ?? []).length > 0 && (
              <div className="space-y-2">
                {submission.fileUrls.map((url, i) => {
                  const ext = url.split('?')[0].split('.').pop()?.toLowerCase() ?? '';
                  const isVideo = ['mp4', 'webm', 'mov', 'avi'].includes(ext);
                  const isAudio = ['mp3', 'wav', 'ogg', 'aac', 'm4a'].includes(ext);
                  if (isVideo) return (
                    <div key={i} className="rounded-xl overflow-hidden border border-cream-200">
                      <video controls className="w-full max-h-64" src={url} />
                    </div>
                  );
                  if (isAudio) return (
                    <div key={i} className="flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
                      <Music size={14} className="text-blue-500 flex-shrink-0" />
                      <audio controls className="flex-1 h-8" src={url} />
                    </div>
                  );
                  return (
                    <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1.5 text-xs bg-white border border-forest-200 text-forest-700 px-3 py-1.5 rounded-full hover:bg-forest-50 transition-colors w-fit">
                      <FileText size={11} /> File {i + 1}
                    </a>
                  );
                })}
              </div>
            )}

            {submission.status === 'GRADED' && (
              <div className="bg-white border border-forest-200 rounded-xl p-4 space-y-2">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1.5">
                    <Star size={16} className="fill-gold-400 text-gold-400" />
                    <span className="font-serif text-2xl font-bold text-forest-900">{submission.score}</span>
                    <span className="text-forest-400 text-sm">/ {activity.maxScore}</span>
                  </div>
                  {submission.score >= activity.passingScore
                    ? <span className="text-xs font-semibold text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full flex items-center gap-1"><CheckCircle2 size={10} /> Passed</span>
                    : <span className="text-xs font-semibold text-rust-600 bg-rust-50 border border-rust-200 px-2 py-0.5 rounded-full">Not Passed</span>}
                </div>
                {submission.feedback && (
                  <div>
                    <p className="text-xs font-semibold text-forest-600 uppercase tracking-widest mb-1">Expert Feedback</p>
                    <p className="text-sm text-forest-700 leading-relaxed italic">"{submission.feedback}"</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Submission form — show if unlocked and not yet graded */}
        {isUnlocked && (!submission || submission.status === 'REVISION_REQUESTED') && (
          <form onSubmit={handleSubmit} className="bg-white border border-cream-200 rounded-2xl p-6 space-y-6">
            <h3 className="font-serif font-semibold text-forest-900">
              {submission?.status === 'REVISION_REQUESTED' ? 'Resubmit Your Work' : 'Submit Your Work'}
            </h3>

            {/* Per-question answer fields */}
            {questions.length > 0 ? (
              <div className="space-y-5">
                {questions.map((q, i) => (
                  <div key={i} className="space-y-2">
                    <div className="flex items-start gap-2.5">
                      <span className="w-6 h-6 bg-forest-800 text-cream-50 rounded-full text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                        {i + 1}
                      </span>
                      <p className="text-sm font-medium text-forest-900 leading-relaxed">{q}</p>
                    </div>
                    <textarea
                      value={answers[i] ?? ''}
                      onChange={(e) => setAnswers(prev => { const a = [...prev]; a[i] = e.target.value; return a; })}
                      rows={4}
                      placeholder={`Your answer to question ${i + 1}...`}
                      className="w-full px-4 py-3 rounded-xl border border-cream-200 text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-cream-50 resize-none leading-relaxed ml-8"
                    />
                    <p className="text-[10px] text-forest-400 ml-8">{(answers[i] ?? '').length} characters</p>
                  </div>
                ))}
              </div>
            ) : (
              // Fallback — no structured questions, show single text area
              <div>
                <label className="block text-xs font-medium text-forest-700 mb-2">Your Response</label>
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  rows={8}
                  placeholder="Write your response here..."
                  className="w-full px-4 py-3 rounded-xl border border-cream-300 text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-cream-50 resize-none leading-relaxed"
                />
                <p className="text-xs text-forest-400 mt-1">{text.length} characters</p>
              </div>
            )}

            {/* File uploads */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-medium text-forest-700">Attach Support documents if required <span className="text-forest-400">(PDF, DOCX, images, video, audio · up to 5 files)</span></label>
                <button type="button" onClick={() => fileRef.current?.click()}
                  className="text-xs text-forest-600 hover:text-forest-900 flex items-center gap-1 font-medium">
                  <Upload size={12} /> Add files
                </button>
              </div>
              <input ref={fileRef} type="file" multiple
                accept=".pdf,.docx,.doc,.png,.jpg,.jpeg,.webp,.mp4,.webm,.mov,.avi,.mp3,.wav,.ogg,.aac,.m4a"
                className="hidden" onChange={handleFileAdd} />

              {files.length > 0 && (
                <div className="space-y-2">
                  {files.map((f, i) => {
                    const cat = getFileCategory(f);
                    const FileIcon = getFileIcon(f);
                    const colorCls = FILE_CATEGORY_COLOR[cat];
                    return (
                      <div key={i} className="flex items-center gap-3 px-3 py-2.5 bg-cream-50 border border-cream-200 rounded-xl">
                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${colorCls}`}>
                          <FileIcon size={13} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-forest-700 truncate font-medium">{f.name}</p>
                          <p className="text-[10px] text-forest-400 capitalize">{cat} · {formatFileSize(f.size)}</p>
                        </div>
                        <button type="button" onClick={() => removeFile(i)} className="text-forest-300 hover:text-rust-500 transition-colors flex-shrink-0">
                          <X size={13} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {files.length === 0 && (
                <button type="button" onClick={() => fileRef.current?.click()}
                  className="w-full border-2 border-dashed border-cream-300 rounded-xl p-6 text-center hover:border-forest-400 transition-colors group">
                  <Upload size={20} className="mx-auto mb-3 text-forest-300 group-hover:text-forest-500 transition-colors" />
                  <p className="text-sm font-medium text-forest-500 group-hover:text-forest-700 transition-colors">Click to add files</p>
                  <div className="flex items-center justify-center gap-3 mt-2 flex-wrap">
                    <span className="flex items-center gap-1 text-[10px] text-purple-500 bg-purple-50 px-2 py-0.5 rounded-full"><Film size={10} /> Video (MP4, MOV, WebM)</span>
                    <span className="flex items-center gap-1 text-[10px] text-blue-500 bg-blue-50 px-2 py-0.5 rounded-full"><Music size={10} /> Audio (MP3, WAV, AAC)</span>
                    <span className="flex items-center gap-1 text-[10px] text-forest-500 bg-forest-50 px-2 py-0.5 rounded-full"><FileText size={10} /> Docs (PDF, DOCX)</span>
                    <span className="flex items-center gap-1 text-[10px] text-green-500 bg-green-50 px-2 py-0.5 rounded-full"><Image size={10} /> Images</span>
                  </div>
                  <p className="text-[10px] text-forest-300 mt-2">Max 500MB per file · Up to 5 files</p>
                </button>
              )}
            </div>

            {questions.length > 0 && (() => {
              const combinedLength = JSON.stringify(questions.map((q, i) => ({ question: q, answer: answers[i]?.trim() ?? '' }))).length;
              const overLimit = combinedLength > 20000;
              const nearLimit = combinedLength > 16000;
              if (!nearLimit) return null;
              return (
                <p className={`text-xs ${overLimit ? 'text-rust-500' : 'text-amber-600'}`}>
                  {overLimit
                    ? `Your combined answers are too long (${combinedLength.toLocaleString()}/20,000 characters). Please shorten them, or move longer sections into an uploaded document.`
                    : `Combined answers: ${combinedLength.toLocaleString()}/20,000 characters`}
                </p>
              );
            })()}

            <button type="submit" disabled={submitting || (questions.length > 0 && JSON.stringify(questions.map((q, i) => ({ question: q, answer: answers[i]?.trim() ?? '' }))).length > 20000)}
              className="btn-primary w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed">
              {submitting
                ? <><Loader2 size={15} className="animate-spin" /> Submitting...</>
                : <><PenLine size={15} /> Submit Assignment</>}
            </button>
          </form>
        )}

      </div>
    </DashboardLayout>
  );
};

export default CanvasPage;